/** R-15 D2 (T-111) — the pure half of a report: the context it captures and the multipart body POST /api/feedback takes. */
import type { FeedbackKind } from '@/kit';

export interface FeedbackContext {
  route: string;
  app: string;
  recordKey: string | null;
  sha: string | null;
  role: string;
  /** R-16 T-125: the registered screen and the panel the pill sat on when its menu opened */
  screenId?: string | null;
  panelId?: string | null;
}

/** The module contract's menu entry for an address: the longest route that contains it whose query it carries. */
export function screenIdFor(pathname: string, search: string, items: readonly { id: string; route: string }[]): string | null {
  const here = new URLSearchParams(search);
  let best: { id: string; score: number } | null = null;
  for (const i of items) {
    const [r = '', q = ''] = (i.route.split('#')[0] ?? '').split('?');
    if (r.length <= 1 || !(pathname === r || pathname.startsWith(`${r}/`))) continue;
    const params = [...new URLSearchParams(q)];
    if (params.some(([k, v]) => k !== 'app' && here.get(k) !== v)) continue;
    const score = r.length * 10 + params.length;
    if (!best || score > best.score) best = { id: i.id, score };
  }
  return best?.id ?? null;
}

/** The About line the pill shows before anyone submits: app · route · panel · record · build. */
export function aboutLine(c: FeedbackContext): string {
  return [c.app, c.route, c.panelId ?? 'no panel', c.recordKey ?? 'no record', c.sha ?? 'unreleased'].join(' · ');
}

const RECORD_PAGE = /^\/registry\/[^/]+\/([^/?#]+)$/;

/** Where the report was raised: the route, the app on screen, the record (the panel's, else ?open=, else the record page's), the serving sha, the roles. */
export function feedbackContext(i: { pathname: string; search: string; app: string | null; recordKey: string | null; sha: string | null; roles: readonly string[]; screenId?: string | null; panelId?: string | null }): FeedbackContext {
  const open = new URLSearchParams(i.search).get('open');
  const page = RECORD_PAGE.exec(i.pathname)?.[1];
  return {
    route: `${i.pathname}${i.search}`,
    app: i.app || 'platform',
    recordKey: i.recordKey || open || (page ? decodeURIComponent(page) : null),
    sha: i.sha,
    role: i.roles.join(','),
    ...(i.screenId !== undefined || i.panelId !== undefined ? { screenId: i.screenId ?? null, panelId: i.panelId ?? null } : {}),
  };
}

export interface FeedbackDraft {
  title: string;
  text: string;
  /** opt-in: an image the person chose */
  image: File | null;
}

export function feedbackBody(kind: FeedbackKind, d: FeedbackDraft, c: FeedbackContext): { ok: true; form: FormData } | { ok: false; error: string } {
  const text = d.text.trim();
  const title = d.title.trim() || text.slice(0, 80);
  if (!text && !title) return { ok: false, error: 'Say in a few words what happened or what you need.' };
  const form = new FormData();
  form.append('kind', kind);
  form.append('title', title);
  form.append('text', text || title); // PR #82: a title alone is a report; the route requires the words
  form.append('context_route', c.route);
  form.append('context_app', c.app);
  if (c.recordKey) form.append('context_record_key', c.recordKey);
  if (c.screenId) form.append('screen_id', c.screenId);
  if (c.panelId) form.append('panel_id', c.panelId);
  if (c.sha) form.append('serving_sha', c.sha);
  form.append('reporter_role', c.role);
  if (d.image) form.append('image', d.image);
  return { ok: true, form };
}

/** #906: the context the form shows read-only — what the server records with the report, the role codes included. */
export function contextRows(c: FeedbackContext): [string, string][] {
  return [
    ['Where', c.route],
    ...(c.screenId !== undefined ? ([['Screen', c.screenId ?? 'None'], ['Panel', c.panelId ?? 'None']] as [string, string][]) : []),
    ['App', c.app],
    ['Record', c.recordKey ?? 'None'],
    ['Version', c.sha ?? 'unreleased'],
    ['Your role', c.role ? c.role.split(',').join(', ') : 'None'],
  ];
}
